import { Component } from '@theme/component';

class CmProductGallery extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.cleanup();
    this.initialize();
  }

  initialize() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.slides = [...this.querySelectorAll('[data-image-id]')];
    this.index = Math.max(0, this.slides.findIndex((slide) => !slide.hidden));
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.paused = this.motion.matches || this.dataset.autoplay === 'false';
    this.hovered = false;
    this.visible = false;
    this.form = this.closest('.cm-shop-form');
    this.controls = this.querySelector('.cm-gallery__controls');
    if (this.controls) this.controls.hidden = false;
    this.querySelector('[data-gallery-previous]')?.addEventListener('click', () => this.show(this.index - 1, true), { signal });
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.show(this.index + 1, true), { signal });
    for (const thumbnail of this.querySelectorAll('[data-gallery-index]')) {
      thumbnail.addEventListener('click', () => this.show(Number(thumbnail.dataset.galleryIndex), true), { signal });
    }
    const thumbnails = this.querySelector('.cm-gallery__thumbnails');
    if (thumbnails) thumbnails.hidden = false;
    this.querySelector('[data-gallery-pause]')?.addEventListener('click', () => {
      this.paused = !this.paused;
      this.updatePause();
      this.schedule();
    }, { signal });
    this.addEventListener('mouseenter', () => { this.hovered = true; this.schedule(); }, { signal });
    this.addEventListener('mouseleave', () => { this.hovered = false; this.schedule(); }, { signal });
    this.addEventListener('focusin', () => this.schedule(), { signal });
    this.addEventListener('focusout', () => queueMicrotask(() => this.schedule()), { signal });
    document.addEventListener('visibilitychange', () => this.schedule(), { signal });
    this.motion.addEventListener('change', () => {
      this.paused = this.motion.matches || this.dataset.autoplay === 'false';
      this.updatePause();
      this.schedule();
    }, { signal });
    this.form?.addEventListener('change', (event) => {
      if (!(event.target instanceof Element)) return;
      const radio = event.target.closest('input[name="variant"]');
      if (!radio) return;
      const index = this.slides.findIndex((slide) => slide.dataset.imageId === radio.dataset.imageId);
      if (index >= 0) this.show(index, true);
      const price = this.form.querySelector('[data-product-price]');
      if (price) price.textContent = radio.dataset.price;
      const button = this.form.querySelector('[data-product-get]');
      if (button) {
        button.disabled = radio.dataset.available !== 'true';
        button.textContent = button.disabled ? button.dataset.labelSoldOut : button.dataset.labelGet;
      }
    }, { signal });
    this.observer = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      this.schedule();
    });
    this.observer.observe(this);
    this.show(this.index);
    this.updatePause();
  }

  show(index, manual = false) {
    if (!this.slides.length) return;
    this.index = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, i) => { slide.hidden = i !== this.index; });
    const count = this.querySelector('[data-gallery-count]');
    if (count) count.textContent = this.slideLabel();
    for (const thumbnail of this.querySelectorAll('[data-gallery-index]')) {
      thumbnail.setAttribute('aria-pressed', String(Number(thumbnail.dataset.galleryIndex) === this.index));
    }
    if (manual) {
      this.paused = true;
      this.updatePause();
      const announcement = this.querySelector('[data-gallery-announcement]');
      if (announcement) announcement.textContent = this.slides[this.index].getAttribute('aria-label');
    }
    this.schedule();
  }

  slideLabel() {
    return this.dataset.slideLabel?.replace('[current]', String(this.index + 1)) ?? '';
  }

  updatePause() {
    const button = this.querySelector('[data-gallery-pause]');
    if (!button) return;
    button.textContent = this.paused ? button.dataset.labelPlay : button.dataset.labelPause;
    button.setAttribute('aria-pressed', String(this.paused));
    button.setAttribute('aria-label', this.paused ? button.dataset.labelPlayAccessible : button.dataset.labelPauseAccessible);
  }

  schedule() {
    clearTimeout(this.timer);
    if (this.paused || this.hovered || !this.visible || document.hidden || this.contains(document.activeElement) || this.slides.length < 2) return;
    this.timer = setTimeout(() => this.show(this.index + 1), 4500);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.cleanup();
  }

  cleanup() {
    clearTimeout(this.timer);
    this.observer?.disconnect();
    this.controller?.abort();
    this.controller = null;
  }
}

if (!customElements.get('cm-product-gallery')) customElements.define('cm-product-gallery', CmProductGallery);
